import { apiClient } from '../utils/api-client';
import type {
  NewRouteSetter,
  RouteSetter,
  RouteSetterRosterEntry,
  RouteSetterSearchResults,
} from '../types/route-setter';

async function detail(response: Response, fallback: string): Promise<string> {
  const body = (await response.json().catch(() => ({}))) as { errors?: Array<{ detail?: string }> };
  return body.errors?.[0]?.detail || fallback;
}

/**
 * Who is credited with setting, backed by
 * `Api::Locations::RouteSettersController`.
 *
 * Everything hangs off a location because that is what decides who may ask and
 * what comes back first. The search deliberately does not: a visiting setter,
 * or the volunteer who bolted the crag, is exactly the person this place's
 * roster does not know yet.
 */
class RouteSettersService {
  /** The picker's opening list, and the roster screen. */
  async roster(locationSlug: string): Promise<RouteSetterRosterEntry[]> {
    const response = await apiClient.get(`/api/locations/${locationSlug}/route_setters`, {
      requireAuth: true,
    });

    if (!response.ok) throw new Error(await detail(response, 'Failed to load route setters'));

    const body: { data: RouteSetterRosterEntry[] } = await response.json();
    return body.data;
  }

  async search(locationSlug: string, query: string): Promise<RouteSetterSearchResults> {
    const response = await apiClient.get(
      `/api/locations/${locationSlug}/route_setters/search?q=${encodeURIComponent(query)}`,
      { requireAuth: true }
    );

    if (!response.ok) throw new Error(await detail(response, 'Search failed'));

    const body: { data: RouteSetterSearchResults } = await response.json();
    return body.data;
  }

  /**
   * Turns an account or a bare name into a setter record, and accredits them
   * here. Both in one call because both are what the picker meant.
   */
  async create(locationSlug: string, attributes: NewRouteSetter): Promise<RouteSetter> {
    const response = await apiClient.post(
      `/api/locations/${locationSlug}/route_setters`,
      { data: { attributes } },
      { requireAuth: true }
    );

    if (!response.ok) throw new Error(await detail(response, 'Failed to add route setter'));

    const body: { data: RouteSetter } = await response.json();
    return body.data;
  }

  /**
   * Fixes a guest's name. Global, like the setter: the same person bolts at
   * several crags, and correcting the spelling corrects all of it.
   */
  async rename(locationSlug: string, setterId: string, name: string): Promise<RouteSetter> {
    const response = await apiClient.patch(
      `/api/locations/${locationSlug}/route_setters/${setterId}`,
      { data: { attributes: { name } } },
      { requireAuth: true }
    );

    if (!response.ok) throw new Error(await detail(response, 'Failed to rename route setter'));

    const body: { data: RouteSetter } = await response.json();
    return body.data;
  }

  /** Takes them off the roster. Does not delete them, or their climbs. */
  async remove(locationSlug: string, setterId: string): Promise<void> {
    const response = await apiClient.delete(
      `/api/locations/${locationSlug}/route_setters/${setterId}`,
      { requireAuth: true }
    );

    if (!response.ok) throw new Error(await detail(response, 'Failed to remove route setter'));
  }
}

export const routeSettersService = new RouteSettersService();
